import {deleteBy, maybeAppendInactive, objectById, urlEncode} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import Vue from 'vue'
import _ from 'lodash'
import {ptracGet, ptracGetEntities} from '@/utilities/ptracApi'
import {OK} from 'http-status-codes'
import {
  byId,
  crudifyMutations,
  hydrateEdit,
  resetResults,
  resetState,
  savePayload,
  updateResult
} from 'src/utilities/vuex2'
import {make} from 'vuex-pathify'

const initialState = {
  edit: {
    id: null,
    name: null,
    types: [],
    phone: null,
    pwsNumber: null,
    notesByDepartment: {},
    fieldNotesByDepartment: {},
    addresses: [],
    contacts: [],
    logo: null,
    quickBooksPreferences: {
      toBePrinted: false,
      toBeEmailed: false,
      onHold: false
    },
    useCreditCard: false,
    poRequired: false,
    active: true
  },
  results: [],
  autocompleteResults: [],
  contactsResults: [],
  autocompleteResultsClient: [],
  autocompleteResultsBuilder: [],
  autocompleteResultsClientContact: [],
  autocompleteResultsBuilderContact: [],
  // used by other modules for quick company lookup
  clientMap: {},
  builderMap: {}
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
getters.byId = byId
getters.savePayload = savePayload

getters.autocompleteResultsClientById = (state) => (id) => {
  return objectById(state.autocompleteResultsClient, id)
}

getters.autocompleteResultsBuilderById = (state) => (id) => {
  return objectById(state.autocompleteResultsBuilder, id)
}

getters.results = (state) => {
  return state.results === undefined ? [] : state.results
}

getters.companyAddresses = (state) => {
  return state.edit.addresses
}

getters.companyContacts = (state) => {
  return state.edit.contacts
}

getters.companyAdminNoteByDepartmentId = (state) => (departmentId) => {
    return state.edit.notesByDepartment[departmentId] || ''
}

getters.companyFieldNoteByDepartmentId = (state) => (departmentId) => {
    return state.edit.fieldNotesByDepartment[departmentId] || ''
}

getters.companyAddressesKvpsForSelect = (state) => {
  const kvps = []
  if (state.edit.addresses && state.edit.addresses.length > 0) {
    state.edit.addresses.forEach((address) => {
      let label = ''
      if (address.location) {
        label = address.location
      } else if (address.streetNumber && address.streetName) {
        label = address.streetNumber + ' ' + address.streetName
      } else if (address.streetName) {
        label = address.streetName
      }

      if (address.city) {
        label += ', ' + address.city
      }

      kvps.push({
        label: label,
        value: address.id
      })
    })
  }

  return kvps
}

const mutations = make.mutations(state)
mutations.reset = resetState
mutations.resetResults = resetResults
mutations.hydrateEdit = hydrateEdit
mutations.updateResult = updateResult
crudifyMutations(mutations)

mutations.resetAutocompleteResults = (state) => {
  state.autocompleteResults = []
}

mutations.appendToResults = (state, result) => {
  state.results.push(result)
}

mutations.resetContactsResults = (state) => {
  state.contactsResults = []
}

mutations.updateContactResult = (state, contact) => {
  if (!state.contactsResults || state.contactsResults.length === 0) {
    return
  }

  let index = _.findIndex(state.contactsResults, contactResult => contactResult.contact.id === contact.id)

  if (index !== -1) {
    state.contactsResults[index].contact = contact
  }
}

mutations.resetEdit = (state) => {
  for (let f in state.edit) {
    state.edit[f] = initialState.edit[f]
  }
  // not sure why the reset logic above is not working on the arrays, manually force back to empty arrays
  state.edit.addresses = []
  state.edit.contacts = []
  state.edit.notesByDepartment = {}
  state.edit.fieldNotesByDepartment = {}
  state.edit.quickBooksPreferences = {
    toBePrinted: false,
    toBeEmailed: false,
    onHold: false
  }
  state.edit.useCreditCard = false
  state.edit.poRequired = false
}

mutations.companyNote = (state, payload) => {
  switch (payload.noteType) {
    case 'admin':
      state.edit.notesByDepartment[payload.departmentId] = payload.note
      break;
    case 'field':
      state.edit.fieldNotesByDepartment[payload.departmentId] = payload.note
      break;
    default:
      console.error("payload.noteType must be 'admin' or 'field'", payload)
  }
}

mutations.updateEdit = (state, newCompany) => {
  state.edit = newCompany
}

mutations.companyAddressAdd = (state, address) => {
  state.edit.addresses.push(address)
}

mutations.companyAddressUpdate = (state, address) => {
  const index = _.findIndex(state.edit.addresses, {id: address.id})
  Vue.set(state.edit.addresses, index, address) // needed to trigger reactivity on arrays
}

mutations.companyAddressRemove = (state, address) => {
  state.edit.addresses = deleteBy(state.edit.addresses, 'id', address.id)

  state.edit.contacts.forEach(contact => {
    if (contact.addressId === address.id) {
      contact.addressId = null
    }
  })
}

mutations.companyContactAdd = (state, contact) => {
  state.edit.contacts.push(contact)
}

mutations.companyContactUpdate = (state, contact) => {
  const index = _.findIndex(state.edit.contacts, {id: contact.id})
  Vue.set(state.edit.contacts, index, contact) // needed to trigger reactivity on arrays
}

mutations.companyContactRemove = (state, contact) => {
  state.edit.contacts = deleteBy(state.edit.contacts, 'id', contact.id)
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchAll({commit}, active) {
      const response = await ptracGetEntities('companies/findAll?active=' + active)

      if (OK === response.status) {
        commit('results', response.data)
      }

      return response
    },
    async fetchActiveForAutocomplete({commit}) {
      const response = await ptracGetEntities('companies/findActiveForSelect')

      if (OK === response.status) {
        commit('autocompleteResults', response.data)
      }

      return response
    },
    async fetchByNameStartsWithAndType({commit}, payload) {
      const response = await ptracGetEntities('companies/search/findByNameStartsWithIgnoreCaseAndTypesOrderByNameAsc?name=' + payload.name + '&types=' + payload.type)

      if (OK === response.status) {
        commit('autocompleteResults' + payload.type, maybeAppendInactive(response.data))
      }

      return response
    },
    async fetchByNameContainsAndTypeAndActive({commit}, payload) {
      let query = 'companies/search/findByNameContainsIgnoreCaseAndActiveOrderByNameAsc?name=' + payload.name + '&active=' + payload.active

      if (payload.name && payload.types.length > 0) {
        query = 'companies/search/findByNameContainsIgnoreCaseAndTypesInAndActiveOrderByNameAsc?name=' + payload.name + '&types=' + payload.types + '&active=' + payload.active
      }

      const response = await ptracGetEntities(query)

      if (OK === response.status) {
        commit('results', response.data)
      }

      return response
    },
    async fetchContacts({commit}, name) {
      const response = await ptracGet('companies/findContactsByNameContains?name=' + urlEncode(name))

      if (OK === response.status) {
        commit('contactsResults', response.data)
      }

      return response
    },
    async fetchContactsForAutocomplete({commit}, payload) {
      const response = await ptracGet('contacts/autocomplete?name=' + urlEncode(payload.name) + '&type=' + payload.type + (payload.companyId ? `&companyId=${payload.companyId}` : ''))

      if (OK === response.status) {
        commit('autocompleteResults' + payload.type + 'Contact', response.data)
      }

      return response
    },
    async fetchByIdsToMap({commit, dispatch}, payload) {
      const response = await dispatch('fetchByIds', {
        endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
        ids: payload.ids
      }, {root: true})

      if (OK === response.status) {
        const companyMap = {}
        response.data.forEach((company) => {
          companyMap[company.id] = company
        })
        commit(payload.type + 'Map', companyMap)
      }
    }
  }
}
